import {ApiRepository, ValidationError} from '../base/api-repository';
import {LabelStore} from '../base/label-store';
import {ViewStore} from '../base/view-store';
import {action, makeObservable, observable} from 'mobx';
import {UserStore} from '../user/user-store';
import {todayAsISOString} from '../base/dateUtils';
import {Capacitor} from '@capacitor/core';

export class MentorStore extends ViewStore {
    @observable
    payload?: MentorAdvicePayload = undefined;
    @observable
    variables: MentorVariables = new MentorVariables();

    constructor(
        private api: ApiRepository,
        labelStore: LabelStore,
        public userStore: UserStore,
    ) {
        super(labelStore);
        makeObservable(this);
    }

    @action
    onEnter(): void {
        this.load(async () => {
            this.variables = new MentorVariables();
            this.payload = await this.getMentorHistory(this.userStore.payload!.employeeId, 'history');
            await this.initializeVariables();
            if (Capacitor.getPlatform() === 'web') {
                const redirectUrl = this.getRedirectUrl();

                window.history.replaceState({}, '', redirectUrl);
            }
            this.loadFreeTextQuestionAction(this.label('mentor.ask-question', {firstName: this.userStore.payload!.fullName.firstName}));
        });
    }

    @action
    onEnterAndLoadInitialAdvice(employeeId: string, catalogQuestionId?: string, questionText?: string, questionScore?: string, filterPeriodId?: string, filterBusinessUnitId?: string, filterEmployeeSegment?: string, filterLocation?: string, filterRole?: string, filterTag?: string, filterServiceLength?: string, filterCategory?: string, filterQuestionId?: string, displayQuestion?: string, summaryType?: string): void {
        this.load(async () => {
            this.variables = new MentorVariables();
            this.variables.catalogQuestionText = questionText;
            this.variables.catalogQuestionScore = questionScore;
            this.variables.displayQuestion = displayQuestion!;

            this.payload = await this.getMentorHistory(employeeId, 'advice');
            await this.initializeVariables();
            if (Capacitor.getPlatform() === 'web') {
                const redirectUrl = this.getRedirectUrl();

                window.history.replaceState({}, '', redirectUrl);
            }
        });
        const dataForMentor: DataForMentor = {
            catalogQuestionId,
            questionText,
            questionScore,
            filterPeriodId,
            filterBusinessUnitId,
            filterEmployeeSegment,
            filterLocation,
            filterRole,
            filterTag,
            filterServiceLength,
            filterCategory,
            filterQuestionId,
            displayQuestion,
            summaryType,
        };

        this.loadMentorAdvice(this.userStore.payload!.employeeId, dataForMentor);
    }

    @action
    onFollowUpChange(value: string): void {
        this.variables.followUpQuestion = value;
    }

    @action
    async onSubmitFollowUpQuestion(value: string): Promise<void> {
        this.variables.actions = undefined;
        if (value === 'submit') {
            const text = this.variables.followUpQuestion;

            if (text !== undefined && text.trim() !== '') {
                this.addEmployeeMessage(text.trim());
                this.deliverMessages();
                const followUp: FollowUpQuestion = {
                    prompt: text.trim(),
                    conversationId: this.variables.conversationId!,
                };

                this.loadMentorAdvice(this.userStore.payload!.employeeId, undefined, followUp);
            } else {
                this.addEmployeeMessage(this.label('mentor.no-follow-up-question'));
                this.addMentorMessage(this.label('mentor.thank-you'));
            }

        } else {
            this.variables = new MentorVariables();
            await this.initializeVariables();
            this.addEmployeeMessage(this.label('mentor.no-follow-up-question'));
            this.addMentorMessage(this.label('mentor.thank-you'));
        }
        this.deliverMessages();
    }

    @action
    async sendMentorAdviceToEmail(conversationId?: string): Promise<void> {
        const employeeId = this.userStore.payload?.employeeId;

        await this.load(async () => {
            try {
                this.payload = await this.api.post<MentorAdvicePayload>(`/mentor/${employeeId}/send-advice/${conversationId}`);
                window.alert(this.label('send-results.sent'));
            } catch (error) {
                this.variables.validationReason = (error as ValidationError).errorMessageCode;
                if (this.variables.validationReason === 'missing-email') {
                    window.alert(this.label('send-results.error'));
                    console.error('Error sending results link:', (error as ValidationError).errorMessageCode);
                }
                console.error('Error sending results link:', (error as ValidationError).errorMessageCode);
            }
        });
    }

    getBotTyping(): boolean {
        return this.variables.isLoading;
    }

    private initializeVariables(): void {
        this.loadInteractionHistory();
        this.addCatalogQuestionDataIfNeeded();
        this.deliverMessages();
    }

    private loadInteractionHistory(): void {
        if (this.payload?.interactions !== undefined && this.payload.interactions.length > 0) {
            this.payload.interactions.forEach((interaction) => {
                const employeeMessage = interaction.question;
                const mentorMessage = interaction.answer;
                const conversationId = interaction.conversationId;

                this.addEmployeeMessage(employeeMessage, conversationId);
                this.addMentorMessage(mentorMessage, conversationId);
            });
        }
    }

    private addCatalogQuestionDataIfNeeded(): void {
        if (this.variables.catalogQuestionText !== undefined && this.variables.catalogQuestionScore !== undefined) {
            this.addEmployeeMessage(this.variables.displayQuestion);
            this.addEmployeeMessage(this.label('mentor.question.prompt'));
        } else if (this.variables.displayQuestion !== undefined) {
            this.addEmployeeMessage(this.variables.displayQuestion);
        }
    }

    private loadMentorAdvice(employeeId: string, dataForMentor?: DataForMentor, followUpQuestion?: FollowUpQuestion): void {
        this.refresh(async () => {
            this.variables.isLoading = true;

            this.payload = await this.fetchMentorAdvice(employeeId, dataForMentor, followUpQuestion);

            if (this.payload.advice !== undefined) {
                this.variables.isLoading = false;

                if (this.payload.advice !== 'tokens_exceeded') {
                    this.addMentorMessage(this.payload.advice);
                    this.deliverMessages();
                    this.loadFreeTextQuestionAction(undefined);
                } else {
                    this.addMentorMessage(this.label(`mentor.${this.payload.advice}`));
                    this.addMentorMessage(this.label('mentor.thank-you'));
                    this.deliverMessages();
                }
            }
            this.variables.isLoading = false;
            if (this.payload.interactions !== undefined && this.payload.interactions.length > 0) {
                this.variables.conversationId = this.payload.interactions[this.payload.interactions.length - 1].conversationId;
            }
        },
        );
    }

    private loadFreeTextQuestionAction(mentorMessage: string | undefined): void {
        this.variables.followUpQuestion = undefined;

        if (this.variables.askFollowUpCounter < 1 && mentorMessage) {
            this.addMentorMessage(mentorMessage);
            this.variables.askFollowUpCounter = this.variables.askFollowUpCounter + 1;
        }
        this.addActionButtons();
        this.deliverMessages();
    }

    private addActionButtons(): void {
        this.variables.actions = [
            {text: this.label('chat.submit'), value: 'submit', color: 'green'},
            {text: this.label('chat.cancel'), value: 'cancel', color: 'white'},
        ];
    }

    private addEmployeeMessage(text: string, conversationId?: string, date: string = todayAsISOString(), color?: 'green' | 'white'): void {
        this.variables.preparedMessages.push({text, party: 'employee', date, color, conversationId});
    }

    private addMentorMessage(text: string, conversationId?: string, date: string = todayAsISOString(), color?: 'green' | 'white', hasClickableLink: boolean = false): void {
        this.variables.preparedMessages.push({text, party: 'mentor', date, color, hasClickableUrl: hasClickableLink, conversationId});
    }

    private deliverMessages(): void {
        while (this.variables.preparedMessages.length !== 0) {
            const firstMessage = this.variables.preparedMessages.shift()!;

            if (firstMessage !== undefined) {
                this.variables.deliveredMessages.push(firstMessage);
            }
        }
    }

    private async getMentorHistory(employeeId: string, context: MentorContext): Promise<MentorAdvicePayload> {
        const contextQueryParam: Record<string, string> = {};

        contextQueryParam.context = context;

        const contextQueryParamString = new URLSearchParams(contextQueryParam).toString();

        return this.api.get<MentorAdvicePayload>(`/mentor/${employeeId}/interactions?${contextQueryParamString}`);
    }

    private async fetchMentorAdvice(employeeId: string, dataForMentor?: DataForMentor, followUpQuestion?: FollowUpQuestion): Promise<MentorAdvicePayload> {
        if (dataForMentor !== undefined) {
            return await this.api.post<MentorAdvicePayload>(`/mentor/${employeeId}/advice`, dataForMentor);
        }
        if (followUpQuestion !== undefined) {
            return await this.api.post<MentorAdvicePayload>(`/mentor/${employeeId}/chat`, followUpQuestion);
        }

        return await this.getMentorHistory(employeeId, 'advice');
    }

    private getRedirectUrl(): string {
        const url = new URL(window.location.href);

        if (url.searchParams.has('catalogQuestionId')) {
            url.searchParams.delete('catalogQuestionId');
        }
        if (url.searchParams.has('questionText')) {
            url.searchParams.delete('questionText');
        }
        if (url.searchParams.has('questionScore')) {
            url.searchParams.delete('questionScore');
        }
        if (url.searchParams.has('filterPeriodId')) {
            url.searchParams.delete('filterPeriodId');
        }
        if (url.searchParams.has('filterBusinessUnitId')) {
            url.searchParams.delete('filterBusinessUnitId');
        }
        if (url.searchParams.has('filterEmployeeSegment')) {
            url.searchParams.delete('filterEmployeeSegment');
        }
        if (url.searchParams.has('filterLocation')) {
            url.searchParams.delete('filterLocation');
        }
        if (url.searchParams.has('filterRole')) {
            url.searchParams.delete('filterRole');
        }
        if (url.searchParams.has('filterTag')) {
            url.searchParams.delete('filterTag');
        }
        if (url.searchParams.has('filterServiceLength')) {
            url.searchParams.delete('filterServiceLength');
        }

        return url.toString();
    }
}

export type MentorContext = 'history' | 'advice';

export interface MentorAdvicePayload {
    interactions: MentorInteraction[],
    context: MentorContext,
    advice?: string,
}

export interface DataForMentor {
    questionText?: string,
    catalogQuestionId?: string,
    questionScore?: string,
    filterPeriodId?: string,
    filterBusinessUnitId?: string,
    filterEmployeeSegment?: string,
    filterLocation?: string,
    filterRole?: string,
    filterTag?: string,
    filterServiceLength?: string,
    filterCategory?: string,
    filterQuestionId?: string,
    displayQuestion?: string,
    summaryType?: string
}

export interface FollowUpQuestion{
    prompt: string,
    conversationId: string
}

export interface MentorInteraction {
    question: string,
    answer: string,
    createdAt: string,
    conversationId: string
}

class MentorVariables {
    @observable
    preparedMessages: MentorMessage[];
    @observable
    deliveredMessages: MentorMessage[];
    @observable
    actions?: MentorActionChoice[];
    @observable
    isLoading: boolean = false;
    @observable
    askFollowUpCounter: number = 0;
    @observable
    followUpQuestion?: string = undefined;
    @observable
    conversationId?: string = undefined;
    @observable
    catalogQuestionText?: string = undefined;
    @observable
    catalogQuestionScore?: string = undefined;
    @observable
    displayQuestion: string;
    @observable
    validationReason?: string = undefined;
    @observable
    showValidation?: boolean = false;

    constructor(preparedMessages: MentorMessage[] = [], deliveredMessages: MentorMessage[] = [], displayQuestion?: string, actions?: MentorActionChoice[], isLoading: boolean = false, aksFollowUpcounter: number = 0, followUpQuestion?: string, conversationId?: string, questionText?: string, score?: string) {
        makeObservable(this);
        this.preparedMessages = preparedMessages;
        this.deliveredMessages = deliveredMessages;
        this.actions = actions;
        this.isLoading = isLoading;
        this.askFollowUpCounter = aksFollowUpcounter;
        this.followUpQuestion = followUpQuestion;
        this.conversationId = conversationId;
        this.catalogQuestionText = questionText;
        this.catalogQuestionScore = score;
        this.displayQuestion = displayQuestion!;
    }
}

export class MentorMessage {
    text: string;
    party: 'employee' | 'mentor';
    date: string;
    color?: 'green' | 'white';
    hasClickableUrl?: boolean = true;
    conversationId?: string;

    constructor(text: string, party: 'employee' | 'mentor', date: string, color: 'green' | 'white' = 'white') {
        makeObservable(this);
        this.text = text;
        this.party = party;
        this.date = date;
        this.color = color;
    }
}

export const copyToClipboard = (message: string): string => {
    const plainText = message
        .replace(/<\/?[^>]+(>|$)/g, '')
        .replace(/[_~`>#+]/g, '')
        .replace(/\*\*(.*?)\*\*/g, '$1')
        .replace(/\*(.*?)\*/g, '$1');

    navigator.clipboard.writeText(plainText);

    return plainText;
};

class MentorActionChoice {
    text: string;
    value: string;
    color: 'green' | 'white';

    constructor(text: string, value: string, color: 'green' | 'white') {
        makeObservable(this);
        this.text = text;
        this.value = value;
        this.color = color;
    }
}
